import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { APP_VERSION } from '../utils/version';
import { Avatar, ROLE_LABELS } from '../components/UI';
import api from '../utils/api';
import UnreadPopup from './UnreadPopup';

const PAGES = {
  '': { label: 'Dashboard', icon: '📊' },
  'leaves': { label: 'Urlopy', icon: '🏖️' },
  'leave-balances': { label: 'Salda urlopowe', icon: '📅' },
  'hours': { label: 'Godziny pracy', icon: '⏱️' },
  'expenses': { label: 'Dodatki do rozliczenia', icon: '🧾' },
  'events': { label: 'Eventy', icon: '🎪' },
  'settlements': { label: 'Rozliczenia', icon: '💰' },
  'staff': { label: 'Kadry', icon: '📇' },
  'structure': { label: 'Struktura firmy', icon: '🌳' },
  'team': { label: 'Mój zespół', icon: '👥' },
  'supplies': { label: 'Zapotrzebowanie', icon: '🛒' },
  'employees': { label: 'Pracownicy', icon: '👥' },
  'documents': { label: 'Dokumenty', icon: '📄' },
  'reports': { label: 'Raporty', icon: '📈' },
  'audit': { label: 'Dziennik zdarzeń', icon: '📜' },
  'availability': { label: 'Moja dostępność', icon: '🗓️' },
  'my-documents': { label: 'Dokumenty', icon: '🪪' },
  'messages': { label: 'Wiadomości', icon: '✉️' },
  'account': { label: 'Moje konto', icon: '🔑' },
  'settings': { label: 'Ustawienia', icon: '⚙️' },
};

export default function Layout() {
  const { canRemoteWork } = useAuth();
  const { user, logout, isAdmin, isManagement, canSettleLeaves, hasLeave, hasAvailability, canApproveHours, canApproveLeaves, canApproveEventHours, hasEventDocs, canSettlePayroll, isEventOnly } = useAuth();
  // T89: ile czeka na decyzję zalogowanego — odświeżane razem z wiadomościami
  const [pending, setPending] = useState(null);
  const { canAssignEvents, canOrderSupplies, canAccounting, seesTeamEvents, hasTeam, seesStaffAbsences, canAddHours, canReviewHours, hasExpenses } = useAuth();
  const loadPending = useCallback(() => { if (isManagement || canSettleLeaves || canAssignEvents || canOrderSupplies) api.getPendingCounts().then(setPending).catch(() => {}); }, [isManagement, canSettleLeaves, canAssignEvents, canOrderSupplies]);
  useEffect(() => {
    loadPending();
    const t = setInterval(loadPending, 60000);
    window.addEventListener('funhr:changed', loadPending); // po zatwierdzeniu / odrzuceniu (api.js)
    return () => { clearInterval(t); window.removeEventListener('funhr:changed', loadPending); };
  }, [loadPending]);
  const location = useLocation();
  useEffect(() => { loadPending(); /* po każdej zmianie ekranu — po zatwierdzeniu liczby od razu aktualne */ }, [location.pathname, loadPending]);
  const [unread, setUnread] = useState([]); // T46: nieprzeczytane — licznik w menu + okno po zalogowaniu
  const loadUnread = useCallback(() => { api.getUnread().then(setUnread).catch(() => {}); }, []);
  useEffect(() => { loadUnread(); const t = setInterval(loadUnread, 60000); return () => clearInterval(t); }, [loadUnread]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const visiblePages = () => {
    if (isAdmin) {
      return ['', 'leaves', 'leave-balances', 'hours', 'expenses', 'events', 'availability', 'supplies', 'settlements',
              'employees', 'structure', 'staff', 'documents', 'reports', 'audit', 'settings', 'messages', 'account'];
    }
    // Osoby zatwierdzające widzą moduły zespołu
    if (isManagement) {
      const m = [''];
      if (hasTeam) m.push('team'); // T124
      if (hasLeave || canRemoteWork || canApproveLeaves || canSettleLeaves) m.push('leaves');
      if (canSettleLeaves || canAccounting) m.push('leave-balances'); // rozlicza urlopy — także z uprawnieniami (T26); księgowość (T114)
      if (canAddHours || canReviewHours) m.push('hours'); // T149: tylko kto wpisuje albo rozpatruje godziny
      if (hasExpenses) m.push('expenses'); // T151/T155: wszystkie dodatki tutaj
      // T118: eventy tylko z uprawnieniem eventowym albo gdy sam jeździ na eventy (samo zatwierdzanie godzin — nie)
      if (seesTeamEvents || user?.eventWorkEnabled) m.push('events');
      m.push('supplies');
      if (canSettlePayroll) m.push('settlements');
      if (canAccounting) m.push('staff');
      if (canAssignEvents || hasAvailability || seesStaffAbsences) m.push('availability'); // T146: dostępność eventowych — tylko układający składy; T127: nieobecności
      if (hasEventDocs) m.push('my-documents');
      m.push('messages', 'account');
      return m;
    }
    // T86: pracownik tylko eventowy — wszystko robi z poziomu eventów (bez Godzin i Zapotrzebowania)
    // Eventowy z „Pracą na magazynie" — dodatkowo „Godziny pracy" (godziny magazynowe wpisuje się tam, nie pod eventem)
    if (isEventOnly) return ['', 'events', ...(user?.warehouseWorkEnabled ? ['hours'] : []), 'availability', 'my-documents', 'messages', 'account'];
    // Zwykły pracownik — urlopy tylko jeśli mu przysługują
    const w = [''];
    if (hasTeam) w.push('team'); // T124
    if (hasLeave || canRemoteWork) w.push('leaves'); // T97: także sam wniosek o pracę zdalną
    if (canSettleLeaves || canAccounting) w.push('leave-balances'); // rozlicza urlopy (T26); księgowość (T114)
    if (canAddHours) w.push('hours'); // T149: Biuro bez pracy na magazynie / eventach — nie ma tu nic do zrobienia
    if (hasExpenses) w.push('expenses'); // T151/T155: wszystkie dodatki tutaj
    if (seesTeamEvents || user?.eventWorkEnabled) w.push('events'); // T145: organizator bez prawa zatwierdzania (np. Biuro układające składy)
    w.push('supplies');
    if (canSettlePayroll) w.push('settlements');
    if (canAccounting) w.push('staff');
    if (canAssignEvents || hasAvailability || seesStaffAbsences) w.push('availability'); // T127: kadry — nieobecności; T145: układający składy
    if (hasEventDocs) w.push('my-documents');
    w.push('messages', 'account');
    return w;
  };

  const handleLogout = () => { logout(); navigate('/login'); };
  const fullName = user ? `${user.firstName} ${user.lastName}` : '';

  const NavContent = () => (
    <>
      <div className="sidebar-brand">
        <div style={{ fontSize: 14, fontWeight: 800, color: '#fff' }}>🎪 FunHR</div>
        <div style={{ fontSize: 9, color: '#94A3B8' }}>Zarządzanie zespołem</div>
      </div>
      <nav className="sidebar-nav">
        {visiblePages().map(p => (
          <NavLink key={p} to={`/${p}`} end={p === ''}
            className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
            onClick={() => setMobileOpen(false)}>
            {PAGES[p]?.icon} {p === 'events' && isEventOnly ? 'Moje eventy' : p === 'availability' && !hasAvailability ? 'Dostępność zespołu' : p === 'availability' && (canAssignEvents || seesStaffAbsences) ? 'Dostępność' : PAGES[p]?.label}
            {p === 'messages' && unread.length > 0 && <span style={{ marginLeft: 'auto', background: '#EF4444', color: '#fff', borderRadius: 99, fontSize: 10, fontWeight: 700, padding: '1px 7px' }}>{unread.length}</span>}
          </NavLink>
        ))}
      </nav>
      <div className="sidebar-user">
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Avatar name={fullName} size={28} />
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fullName}</div>
            <div style={{ fontSize: 9, color: '#64748B' }}>{ROLE_LABELS[user?.role] || user?.role} · v{APP_VERSION}</div>
          </div>
          <button onClick={handleLogout} title="Wyloguj"
            style={{ background: 'none', border: 'none', color: '#64748B', cursor: 'pointer', fontSize: 14 }}>
            ⏻
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="app-layout">
      <aside className={`sidebar ${mobileOpen ? 'mobile-open' : ''}`}>
        <NavContent />
      </aside>
      {mobileOpen && <div className="mobile-overlay" onClick={() => setMobileOpen(false)} />}

      <main className="main-content">
        <header className="top-bar">
          <div className="flex items-center gap-8">
            <button className="mobile-menu-btn btn btn-ghost btn-sm"
              onClick={() => setMobileOpen(!mobileOpen)}>☰</button>
            <span style={{ fontSize: 12, fontWeight: 700 }}>FunHR</span>
          </div>
          <div style={{ fontSize: 10, color: '#94A3B8' }}>
            {fullName}
          </div>
        </header>

        {pending && (() => {
          // T89: konkretne liczby z linkami; pasek znika, gdy nic nie czeka
          // odmiana: 1 wpis, 2–4 wpisy (poza 12–14), 5+ wpisów
          const pl = (n, one, few, many) => n === 1 ? one : (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14)) ? few : many;
          const items = [
            [pending.hours, pl(pending.hours, 'wpis godzin', 'wpisy godzin', 'wpisów godzin'), '/hours?scope=team'],
            [pending.bonuses, pl(pending.bonuses, 'dodatek', 'dodatki', 'dodatków'), '/expenses?scope=team'], // T155
            [pending.leaves, pl(pending.leaves, 'wniosek urlopowy', 'wnioski urlopowe', 'wniosków urlopowych'), '/leaves'],
            [pending.documents, pl(pending.documents, 'dokument', 'dokumenty', 'dokumentów'), '/'],
            [pending.leavesToSettle, pl(pending.leavesToSettle, 'urlop do wprowadzenia do Enovy', 'urlopy do wprowadzenia do Enovy', 'urlopów do wprowadzenia do Enovy'), '/leaves'],
            [pending.teamChanges, pl(pending.teamChanges, 'odwołanie w składach', 'odwołania w składach', 'odwołań w składach'), '/#zmiany-w-skladach'],
            [pending.supplies, pl(pending.supplies, 'zapotrzebowanie do rozpatrzenia', 'zapotrzebowania do rozpatrzenia', 'zapotrzebowań do rozpatrzenia'), '/supplies?status=PENDING'],
            [pending.suppliesToOrder, pl(pending.suppliesToOrder, 'zapotrzebowanie do zamówienia', 'zapotrzebowania do zamówienia', 'zapotrzebowań do zamówienia'), '/supplies?status=APPROVED'],
          ].filter(([n]) => n > 0);
          if (!items.length) return null;
          return (
            <div className="alert-bar">
              ⚠️ <b>Czeka na Ciebie:</b>{' '}
              {items.map(([n, label, to], i) => (
                <span key={label}>{i > 0 && ' · '}<Link to={to} style={{ color: 'inherit', fontWeight: 600 }}>{n} {label}</Link></span>
              ))}
            </div>
          );
        })()}

        <div className="content-area">
          <Outlet context={{ refreshUnread: loadUnread }} />
        </div>
        {unread.length > 0 && <UnreadPopup messages={unread} onDone={loadUnread} />}
      </main>
    </div>
  );
}
